import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Select,
  MenuItem,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { Search as SearchIcon, Add as AddIcon } from "@mui/icons-material";

const CreateAdditionalApplication = () => {
  // ---------- State ----------
  const [formData, setFormData] = useState({
    userName: "",
    selectedApplication: "",
  });

  const [applications, setApplications] = useState([]);
  const [selectedAppData, setSelectedAppData] = useState(null); // { AppId, OrgId, Name }
  const [assessmentTypes, setAssessmentTypes] = useState([]);
  const [selectedAssessmentType, setSelectedAssessmentType] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showApplicationDropdown, setShowApplicationDropdown] = useState(false);

  // Password dialog state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [verifying, setVerifying] = useState(false);

  // ---------- Fetch Assessment Types ----------
  useEffect(() => {
    const fetchAssessmentTypes = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/common/get-assessment-type`
        );
        if (res.data.success) {
          setAssessmentTypes(res.data.data || []);
        } else {
          console.warn("No assessment types found");
        }
      } catch (err) {
        console.error("Error fetching assessment types:", err);
      }
    };
    fetchAssessmentTypes();
  }, []);

  // ---------- Handlers ----------
  const handleSearchUser = async () => {
    if (!formData.userName.trim()) {
      setError("Please enter a username");
      return;
    }

    setLoading(true);
    setError("");
    setSuccessMessage("");
    setSelectedAppData(null);
    setSelectedAssessmentType("");
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/common/applications-by-username-by-7d15d/${formData.userName}`
      );
      if (res.data.success && res.data.data && res.data.data.length > 0) {
        setApplications(res.data.data);
        setShowApplicationDropdown(true);
      } else {
        setError("No applications found for this username");
        setApplications([]);
        setShowApplicationDropdown(false);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error searching applications");
      setApplications([]);
      setShowApplicationDropdown(false);
    } finally {
      setLoading(false);
    }
  };

  const handleApplicationChange = (e) => {
    const appId = e.target.value;
    setFormData({ ...formData, selectedApplication: appId });

    const selectedApp = applications.find((app) => app.AppId === appId);
    if (selectedApp) {
      setSelectedAppData({
        AppId: selectedApp.AppId,
        OrgId: selectedApp.OrgId,
        Name: selectedApp.Name,
        oldappid: selectedApp.AppId,
        LabTypeId: selectedApp.LabTypeId,
      });
    } else {
      setSelectedAppData(null);
    }
    setSelectedAssessmentType("");
  };

  // ---------- Password Dialog ----------
  const handleCreateClick = () => {
    if (!selectedAppData) {
      setError("Please select an application first.");
      return;
    }
    if (!selectedAssessmentType) {
      setError("Please select an application type.");
      return;
    }
    setPasswordDialogOpen(true);
    setPassword("");
    setError("");
  };

  const handlePasswordSubmit = async () => {
    if (!password.trim()) {
      setError("Please enter your password");
      return;
    }

    setVerifying(true);
    setError("");

    try {
      // 1. Verify password
      const passwordResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/verify-password`,
        {
          orgId: localStorage.getItem("orgId"),
          password: password,
        }
      );

      if (!passwordResponse.data.success) {
        setError("Incorrect password. Please try again.");
        setVerifying(false);
        return;
      }

      // 2. Password verified – create application
      const createPayload = {
        orgId: selectedAppData.OrgId,
        oldappid: selectedAppData.oldappid,
        assessmentTypeId: selectedAssessmentType,
        labTypeId: selectedAppData.LabTypeId,
        createdById: localStorage.getItem("orgId"),
      };

      const createRes = await axios.post(
        `${process.env.REACT_APP_API_URL}/common/create-application`,
        createPayload
      );

      if (createRes.data.success) {
        setSuccessMessage(`Application created successfully!`);
        setPasswordDialogOpen(false);
        setPassword("");
        setSelectedAssessmentType("");
      } else {
        setError(createRes.data.message || "Failed to create application");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error creating application");
    } finally {
      setVerifying(false);
    }
  };

  const handleClosePasswordDialog = () => {
    setPasswordDialogOpen(false);
    setPassword("");
    setError("");
    setVerifying(false);
  };

  // ---------- Render ----------
  return (
    <Box sx={{ p: 2 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <Typography variant="h4" sx={{ flexGrow: 1, fontWeight: "bold" }}>
              Create Application
            </Typography>
          </Box>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>
                Only the following application types can be created:
              </strong>
            </Typography>

            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}>
              <li>Renewal of Accreditation</li>
              <li>Mid Cycle</li>
              <li>Surveillance 1 (Onsite)</li>
              <li>Surveillance 2 (Onsite)</li>
              <li>Re-issue of Certificate</li>
            </Box>
          </Alert>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          {successMessage && (
            <Alert
              severity="success"
              sx={{ mb: 2 }}
              onClose={() => setSuccessMessage("")}
            >
              {successMessage}
            </Alert>
          )}

          {/* Search Section */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <Typography variant="body1" sx={{ textAlign: "right" }}>
                  User Name / LAB-ID <span style={{ color: "red" }}>*</span>
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  name="userName"
                  value={formData.userName}
                  onChange={(e) =>
                    setFormData({ ...formData, userName: e.target.value })
                  }
                  placeholder="Enter User Name / LAB-ID"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SearchIcon />}
                  onClick={handleSearchUser}
                  disabled={loading}
                >
                  Search
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Application Dropdown Section */}
          {showApplicationDropdown && (
            <>
              <Divider sx={{ my: 2 }} />
              <Paper sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={5}>
                    <Typography variant="body1" sx={{ textAlign: "right" }}>
                      Select Source Application (Data will be copied from this
                      application) <span style={{ color: "red" }}>*</span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={7}>
                    <FormControl fullWidth size="small">
                      <Select
                        value={formData.selectedApplication}
                        onChange={handleApplicationChange}
                        displayEmpty
                      >
                        <MenuItem value="">
                          -----------Select Application----------
                        </MenuItem>
                        {applications.map((app) => (
                          <MenuItem key={app.AppId} value={app.AppId}>
                            {app.Name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Paper>
            </>
          )}

          {/* Application Type & Create Button */}
          {selectedAppData && (
            <>
              <Divider sx={{ my: 2 }} />
              <Paper sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={5}>
                    {/* ✅ Updated label */}
                    <Typography variant="body1" sx={{ textAlign: "right" }}>
                      Select the type of application you want to create{" "}
                      <span style={{ color: "red" }}>*</span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={7}>
                    <FormControl fullWidth size="small">
                      <Select
                        value={selectedAssessmentType}
                        onChange={(e) =>
                          setSelectedAssessmentType(e.target.value)
                        }
                        displayEmpty
                      >
                        <MenuItem value="">
                          -----------Select Application Type----------
                        </MenuItem>
                        {assessmentTypes.map((type) => (
                          <MenuItem key={type.id} value={type.id}>
                            {type.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                {/* Button below */}
                <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<AddIcon />}
                    onClick={handleCreateClick}
                    disabled={!selectedAssessmentType || loading}
                    sx={{ px: 4, py: 1.5 }}
                  >
                    Create Application
                  </Button>
                </Box>
              </Paper>
            </>
          )}
        </CardContent>
      </Card>

      {/* Password Verification Dialog */}
      <Dialog open={passwordDialogOpen} onClose={handleClosePasswordDialog}>
        <DialogTitle>Verify Password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please enter your password to confirm the creation of this
            application.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Password"
            type="password"
            fullWidth
            variant="outlined"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handlePasswordSubmit()}
            disabled={verifying}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePasswordDialog} disabled={verifying}>
            Cancel
          </Button>
          <Button
            onClick={handlePasswordSubmit}
            variant="contained"
            color="primary"
            disabled={verifying || !password.trim()}
            startIcon={verifying ? <CircularProgress size={20} /> : null}
          >
            {verifying ? "Verifying..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreateAdditionalApplication;